/**
 * Universal Call Types & Future Retell Agent Contract
 * 
 * Strict boundary rules:
 * 1. Human-controlled execution only (no automated triggers, no webhooks initiating calls).
 * 2. Temporary call packet edits are call-scoped and must never mutate chart, physician, auth, POC, billing, or payroll.
 * 3. Client and staff internal database IDs remain server-side; not passed to external telephony agents.
 * 4. Missing fields must resolve to "Unavailable" without fabricated defaults.
 */

export type UniversalCallPurpose = 'VERIFY_MEMBER_ACTIVE_FAX' | 'CHECK_POC';

export interface UniversalCallPurposeConfig {
  id: UniversalCallPurpose;
  label: string;
  tagline: string;
  description: string;
  targetDepartmentDefault: string;
}

export const UNIVERSAL_CALL_PURPOSES: Record<UniversalCallPurpose, UniversalCallPurposeConfig> = {
  VERIFY_MEMBER_ACTIVE_FAX: {
    id: 'VERIFY_MEMBER_ACTIVE_FAX',
    label: 'VERIFY MEMBER / ACTIVE-FAX',
    tagline: 'Physician / Practice Electronic Fax Verification',
    description: 'Contact physician or clinic to verify member identity on file and confirm active electronic fax reception for speech therapy referrals and orders.',
    targetDepartmentDefault: 'Medical Records / Front Desk'
  },
  CHECK_POC: {
    id: 'CHECK_POC',
    label: 'CHECK POC',
    tagline: 'Plan of Care Signature & Certification Follow-up',
    description: 'Contact physician office to follow up on the Speech Therapy Plan of Care awaiting physician review or signature.',
    targetDepartmentDefault: 'Medical Records / Referrals'
  }
};

/**
 * Retell Agent & Version Configuration Contract
 * 
 * STRICT BOUNDARY RULES:
 * 1. Pinned to Allegiant Office — Universal Call: agent_95629e322f5df04747ac7a3f71, version: "0".
 * 2. The Allegiant Therapy workspace is selected by RETELL_API_KEY. This file does not send a workspace id.
 * 3. Never use latest, never fall back to another agent.
 * 4. Outbound dispatch requires all: RETELL_API_KEY, RETELL_FROM_NUMBER, approved agent ID, approved version, and verified webhook receiver.
 */
export interface RetellAgentConfiguration {
  agent_id: string;
  agent_version: string;
  workspace_id: string;
  status: 'APPROVED_PUBLISHED';
  published: boolean;
  notes: string;
}

export const PINNED_RETELL_OFFICE_AGENT_ID = 'agent_95629e322f5df04747ac7a3f71';
export const PINNED_RETELL_OFFICE_AGENT_VERSION = '0';
export const PINNED_RETELL_OFFICE_AGENT_VERSION_NUM = 0;
/** Local label only. Not sent to Retell. The API key selects the workspace. */
export const PINNED_RETELL_WORKSPACE_ID = 'org_UmodgLBJyQLOVp82';

export const RETELL_OFFICE_AGENT_CONFIG: RetellAgentConfiguration = {
  agent_id: PINNED_RETELL_OFFICE_AGENT_ID,
  agent_version: PINNED_RETELL_OFFICE_AGENT_VERSION,
  workspace_id: PINNED_RETELL_WORKSPACE_ID,
  status: 'APPROVED_PUBLISHED',
  published: true,
  notes: 'Pinned to Allegiant Office — Universal Call, version 0.'
};

/**
 * Validates whether the agent configuration is complete and approved for dispatch.
 * Both approved agent ID and published version must be present and published.
 */
export function isAgentDispatchReady(config: RetellAgentConfiguration = RETELL_OFFICE_AGENT_CONFIG): {
  ready: boolean;
  reason: string;
} {
  if (!config.agent_id) {
    return {
      ready: false,
      reason: 'Agent ID is UNCONFIGURED.'
    };
  }
  if (!config.agent_version) {
    return {
      ready: false,
      reason: 'Agent version is UNCONFIGURED.'
    };
  }
  if (!config.published || config.status !== 'APPROVED_PUBLISHED') {
    return {
      ready: false,
      reason: 'Agent is not yet published and approved.'
    };
  }
  return { ready: true, reason: 'Approved published agent configured.' };
}

/**
 * Reviewed & editable call packet.
 * Any edits by staff are flagged in `edited_fields` and labeled "For this call only".
 */
export interface UniversalCallPacket {
  // Target practice / clinic details
  practice_name: string;
  target_phone: string;
  department: string;

  // Member Identity (Prefilled from Chart)
  member_first_name: string;
  member_last_name: string;
  member_dob: string;       // YYYY-MM-DD or 'Unavailable'
  member_ahcccs_id: string; // AHCCCS ID or 'Unavailable'

  // Purpose 1 Facts: VERIFY MEMBER / ACTIVE-FAX
  active_fax_number?: string;
  recent_fax_date?: string;
  recent_fax_document_type?: string;
  active_order_status?: string;

  // Purpose 2 Facts: CHECK POC
  poc_document_title?: string;
  poc_document_id?: string;
  poc_date_sent?: string;              // Outgoing fax sent date (YYYY-MM-DD) or 'Unavailable' (from actual fax record)
  poc_fax_sent_at?: string;            // Outgoing fax transmission timestamp or 'Unavailable'
  poc_fax_destination?: string;        // Outgoing fax destination number or 'Unavailable'
  poc_fax_delivery_status?: string;    // Outgoing fax delivery evidence / status or 'Unavailable'
  allegiant_return_fax?: string;       // Authoritative Allegiant return fax or 'Unavailable'
  poc_fax_evidence_link?: 'EXPLICIT_ID_LINK' | 'UNVERIFIED_CANDIDATE' | 'NONE'; // Strict link verification status
  poc_fax_candidate_note?: string;     // Note describing unverified title-only candidate for staff review
  poc_end_date?: string;
  physician_name?: string;
  request_type?: string;

  // Staff instructions & notes
  staff_instructions: string;

  // Address, demographic & contact facts
  address?: string;
  member_address?: string;
  member_phone?: string;
  guardian_name?: string;
  guardian_phone?: string;
  office_fax_on_file?: string;
  target_name?: string;
  target_department?: string;
  routing_instructions?: string;
  delivery_evidence_summary?: string;
  fax_sent_at?: string;
  fax_sent_to?: string;
  poc_identifier?: string;
  poc_title?: string;
  call_purpose?: string;
  dob?: string;

  // Tracking field edits: true if edited by staff for this call
  edited_fields: Record<string, boolean>;
}

/**
 * Flat Retell Agent Dynamic Variables Contract (Allegiant Office — Universal Call, version 0)
 * Every field is guaranteed to be a string. Nested structures are strictly excluded.
 */
export interface RetellLlmDynamicVariables {
  // Required
  call_purpose: string;
  target_name: string;
  member_first_name: string;
  member_last_name: string;
  dob: string;

  // Optional
  target_department: string;
  address: string;
  member_phone: string;
  guardian_name: string;
  guardian_phone: string;
  physician_name: string;
  office_fax_on_file: string;
  poc_identifier: string;
  poc_title: string;
  fax_sent_at: string;
  fax_sent_to: string;
  delivery_evidence_summary: string;
  allegiant_return_fax: string;
  staff_instructions: string;
  routing_instructions: string;
}

/**
 * Exact Outbound Retell Call Request Shape for POST /v2/create-phone-call
 */
export interface RetellOutboundCallRequest {
  from_number: string;
  to_number: string;
  override_agent_id: string;
  override_agent_version: number;
  retell_llm_dynamic_variables: RetellLlmDynamicVariables;
  metadata: {
    intent_id: string;
    client_id: string | null;
    staff_user_id: string;
    purpose: string;
  };
}

/**
 * Future Retell Agent Telephony Contract Payload
 * Kept for backwards compatibility and preview references.
 */
export interface RetellOfficeAgentPayload extends Partial<RetellLlmDynamicVariables> {
  agent_id?: string;
  agent_version?: string;
  to_number?: string;
  purpose?: UniversalCallPurpose;
  purpose_label?: string;
  member_identity?: any;
  physician_practice?: any;
  optional_routing_context?: any;
  verification_context?: any;
  poc_context?: any;
  [key: string]: any;
}

/**
 * Future One-Use Call Intent
 * Binds authenticated staff user, selected client, purpose, reviewed phone, and frozen packet.
 * Only an explicit human click may initiate execution once connected.
 */
export interface UniversalCallIntent {
  id: string; // Unique call_request_id (e.g., call_req_...)
  created_at: string;
  staff_user_id: string; // Stored server-side only
  staff_user_name: string;
  client_id: string | null; // Stored server-side only. Null when the call is not associated with a chart.
  client_name: string;
  purpose: UniversalCallPurpose;
  reviewed_target_phone: string;
  frozen_packet: UniversalCallPacket;
  retell_payload: RetellLlmDynamicVariables;
  status: 'PREPARED' | 'READY_FOR_CLICK' | 'LOCKED' | 'CONSUMED' | 'MINTED' | 'IN_FLIGHT' | 'DISPATCHED' | 'DISPATCH_FAILED';
}

/**
 * Normalized Result Contract
 * Returned after future call analysis.
 */
export interface UniversalCallResult {
  call_request_id: string;
  retell_call_id: string;
  call_status: 'COMPLETED' | 'FAILED' | 'BUSY' | 'NO_ANSWER' | 'VOICEMAIL' | 'TRANSFERRED';
  human_reached: true | false | 'unknown';
  person_reached?: string;
  department_reached?: string;
  concise_outcome: string;
  duration_seconds: number;
  failure_reason?: string;
  office_requested?: string; // e.g. "Office requested re-faxing to 480-555-0199"
}
